import type { FieldType } from "@/lib/api/types";

/** Which constraint sub-editors the builder shows for a field type. */
export type ConstraintKey =
  | "minMax" // min / max / step
  | "unit" // unit-of-measure label
  | "options" // enum options list
  | "multiple" // enum: allow multiple
  | "asPercent" // ratio: display as %
  | "maxLength" // text / richtext
  | "pattern" // text
  | "durationUnit" // duration: ms | s (stored in `unit`)
  | "refTarget"; // ref: target + participantType

/** How A2 renders a field for data entry. */
export type WidgetKind = "number" | "text" | "checkbox" | "select" | "textarea" | "unsupported";

export type FieldTypeSpec = {
  type: FieldType;
  label: string;
  widget: WidgetKind;
  constraints: ConstraintKey[];
  supported: boolean; // object/array are deferred (recursive editor) → false
};

/** Single source of truth for the catalog types, shared by the data-entry
 * renderer (A2 FieldSetForm) and the builder's FieldEditor. */
export const FIELD_TYPES: FieldTypeSpec[] = [
  { type: "text", label: "Text", widget: "text", constraints: ["maxLength", "pattern"], supported: true },
  { type: "integer", label: "Integer", widget: "number", constraints: ["minMax", "unit"], supported: true },
  { type: "number", label: "Number", widget: "number", constraints: ["minMax", "unit"], supported: true },
  { type: "ratio", label: "Ratio (0–1)", widget: "number", constraints: ["asPercent"], supported: true },
  { type: "boolean", label: "Boolean", widget: "checkbox", constraints: [], supported: true },
  { type: "enum", label: "Enum (choice)", widget: "select", constraints: ["options", "multiple"], supported: true },
  { type: "duration", label: "Duration", widget: "number", constraints: ["durationUnit"], supported: true },
  { type: "date", label: "Date", widget: "text", constraints: [], supported: true },
  { type: "datetime", label: "Datetime", widget: "text", constraints: [], supported: true },
  { type: "ref", label: "Reference", widget: "text", constraints: ["refTarget"], supported: true },
  { type: "country", label: "Country (ISO-3)", widget: "text", constraints: [], supported: true },
  { type: "richtext", label: "Rich text", widget: "textarea", constraints: ["maxLength"], supported: true },
  // Deferred — recursive nested-field editors (P2b-2 out of scope).
  { type: "object", label: "Object (nested)", widget: "unsupported", constraints: [], supported: false },
  { type: "array", label: "Array (list)", widget: "unsupported", constraints: [], supported: false },
];

export const FIELD_TYPE_BY_KEY = Object.fromEntries(
  FIELD_TYPES.map((spec) => [spec.type, spec]),
) as Record<FieldType, FieldTypeSpec>;

export function widgetFor(type: FieldType): WidgetKind {
  return FIELD_TYPE_BY_KEY[type]?.widget ?? "text";
}
